import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'src/makeStyles';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';

import ExpandMore from '../ExpandMore';
import CardHeaderLabels from '../CardHeaderLabels';
import NextLink from '../NextLink';
import TitledLinks from '../TitledLinks';
import { colors } from '../../constants/styleConstants';
import styles from './ListingExpandableCard.module.scss';

const useStyles = makeStyles()(theme => ({
  root: {
    width: '100%',
    backgroundColor: colors.white,
    boxShadow: 'none',
    borderBottom: `1px solid ${colors.textGray}`,
    borderRadius: 0,
  },
  header: {
    padding: '16px 0',
  },
  contentWrapper: {
    display: 'flex',
    flex: '1 1 0',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
  },
  contentContainer: {
    marginLeft: 60,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 50,
    },
  },
  collapsed: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  collapsedContentWrapper: {
    display: 'flex',
    padding: '0 0 13px',
  },
  expandBlock: {
    width: '60px',
    minWidth: '60px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      width: '50px',
      minWidth: '50px',
    },
  },
  mainContent: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  infoContent: {
    display: 'flex',
    flex: '1 1 0',
  },
  actionContent: {
    display: 'flex',
    alignItems: 'center',
    margin: '1rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginLeft: 50,
    },
  },
  expandCard: {
    display: 'flex',
    justifyContent: 'center',
    '& span': {
      display: 'none',
    },
    '&:hover, &:active, &:focus': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    '& svg': {
      margin: 0,
    },
  },
  expandArrow: {
    color: `${colors.cardTitle} !important`,
  },
  viewDetails: {
    padding: '6px 10px',
    border: `1px solid ${colors.gray}`,
    borderRadius: '2px',
    fontWeight: 'bold',
    [theme.breakpoints.down('md')]: {
      lineHeight: 1,
      textAlign: 'center',
      minWidth: '120px',
    },
  },
  lineWrapper: {
    display: 'flex',
    width: '60px',
    minWidth: '60px',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      width: '50px',
      minWidth: '50px',
    },
  },
  verticalLine: {
    width: '2px',
    background: colors.textGray,
  },
  titledLinksRoot: {
    [theme.breakpoints.down('md')]: {
      flexWrap: 'nowrap',
    },
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  collapsedContent: {
    width: '100%',
    '& > div': {
      marginBottom: theme.spacing(1),
    },
    '& > div:last-child': {
      marginBottom: 0,
    },
  },
  cardsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  viewAllLink: {
    marginTop: 5,
  },
}));

const ListingExpandableCard = ({
  labels,
  t,
  source,
  detailsLinkProps,
  viewDetailsText,
  collapsedContent,
  viewAllBtn,
  expandBlockClass,
  className,
  onExpand,
}) => {
  const { classes, cx } = useStyles();
  const [expanded, setExpanded] = React.useState(false);

  const handleExpandClick = () => {
    onExpand && onExpand();
    setExpanded(!expanded);
  };

  const content = (
    <CardHeaderLabels
      labels={labels}
      source={source}
      className={classes.header}
      titledLinksClassName={classes.titledLinksRoot}
    />
  );

  return (
    <Card className={cx(classes.root, className)}>
      <div className={classes.mainContent}>
        <div className={classes.infoContent}>
          {collapsedContent && (
            <div className={cx(classes.expandBlock, expandBlockClass)}>
              <ExpandMore
                expanded={expanded}
                onClick={handleExpandClick}
                className={styles.expandCard}
                noTitle
                arrowClassName={styles.expandArrow}
              />
            </div>
          )}
          <div className={cx(classes.contentWrapper, { [classes.contentContainer]: !collapsedContent })}>
            {content}
          </div>
        </div>
        <div className={classes.actionContent}>
          {detailsLinkProps && (
            <NextLink {...detailsLinkProps} className={classes.viewDetails}>
              {viewDetailsText}
            </NextLink>
          )}
        </div>
      </div>
      {collapsedContent && (
        <>
          <Collapse className={classes.collapsed} in={expanded} timeout="auto" unmountOnExit>
            <div className={classes.collapsedContentWrapper}>
              <div className={classes.lineWrapper}>
                <span className={classes.verticalLine} />
              </div>
              <div className={classes.cardsWrapper}>
                <div className={classes.collapsedContent}>{collapsedContent}</div>
                {viewAllBtn && (
                  <div className={classes.viewAllLink}>
                    <NextLink href={{ pathname: viewAllBtn.pathname, query: viewAllBtn.query }}>
                      {t('operabase.general.SIDEBAR_VIEW_COUNT_TEMPLATE', { number: viewAllBtn.count })}
                    </NextLink>
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </>
      )}
    </Card>
  );
};

ListingExpandableCard.propTypes = {
  labels: TitledLinks.propTypes.labels,
  source: PropTypes.object.isRequired,
  detailsLinkProps: PropTypes.oneOfType([PropTypes.shape({ ...NextLink.propTypes }), PropTypes.func]),
  collapsedContent: PropTypes.node,
  expandBlockClass: PropTypes.string,
  viewDetailsText: PropTypes.string,
  className: PropTypes.string,
};

export default ListingExpandableCard;
